import { ReactNode, useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiFetcher } from '@/data/fetcher'
import { Collection } from '@/interfaces/Collection'
import { Settings } from '@/interfaces/Settings'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { CatalogSku, parseMajorAmount } from '@/lib/catalog/catalogTypes'
import { components } from '@invo.space/shared'

type TaxRate = components['schemas']['TaxRate']

const isDefaultRate = (rate: TaxRate) => rate.isDefault === 'yes'

interface PriceEditPopoverProps {
  sku: CatalogSku
  title?: string
  description?: string
  onSaved?: () => void
  children: ReactNode
}

const toInput = (amount?: string | number | null) => {
  if (amount === undefined || amount === null || amount === '') return ''
  const value = Number(amount) / 100
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace('.', ',')
}

/** Rychlá změna ceny varianty platná od dneška. Plánované ceny a poznámka jsou v Historii cen. */
export const PriceEditPopover = ({ sku, title = 'Změnit cenu', description, onSaved, children }: PriceEditPopoverProps) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [taxRateId, setTaxRateId] = useState<string>('')
  const [saving, setSaving] = useState(false)

  const { data: settings } = useSWR<Settings>('/api/v2/settings', apiFetcher)
  const { data: taxRates } = useSWR<Collection<TaxRate>>(open ? '/api/v2/tax-rates' : null, apiFetcher)

  const isVatPayer = settings?.vatPayer === 'vatPayer'
  const currency = sku.price?.price?.currency ?? settings?.primaryCurrency ?? 'CZK'
  const amount = parseMajorAmount(value)
  const defaultTaxRate = taxRates?.data.find((rate) => isDefaultRate(rate)) ?? taxRates?.data[0]
  const selectedTaxRateId = taxRateId || String(sku.price?.taxRate?.id ?? defaultTaxRate?.id ?? '')

  const save = async () => {
    if (amount === null || amount <= 0) return
    setSaving(true)
    try {
      const taxRate = taxRates?.data.find((rate) => String(rate.id) === selectedTaxRateId)
      const payload: components['schemas']['SkuPriceCreate'] = {
        price: { amount: String(amount), currency },
        taxRate: isVatPayer ? taxRate?.id : undefined,
        validFrom: null,
        note: null,
      }
      await API.post(`/api/v2/skus/${sku.id}/prices`, payload)
      toast.success('Cena uložena')
      setOpen(false)
      onSaved?.()
    } catch (error: unknown) {
      toast.error('Cenu se nepodařilo uložit', { description: extractErrorMessage(error) || undefined })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (o) {
          setValue(toInput(sku.price?.price?.amount))
          setTaxRateId('')
        }
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
        >
          <div className="border-b px-4 py-3">
            <p className="text-sm font-medium">{title}</p>
            {description && <p className="truncate text-xs text-muted-foreground">{description}</p>}
          </div>
          <div className="space-y-3 px-4 py-3">
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Cena</span>
              <div className="relative">
                <Input
                  autoFocus
                  inputMode="decimal"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="0"
                  className="pr-12 text-right tabular"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  {currency === 'CZK' ? 'Kč' : currency}
                </span>
              </div>
            </label>
            {isVatPayer && (
              <label className="block space-y-1">
                <span className="text-xs text-muted-foreground">Sazba DPH</span>
                <Select value={selectedTaxRateId} onValueChange={setTaxRateId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Vyberte sazbu" />
                  </SelectTrigger>
                  <SelectContent>
                    {taxRates?.data.map((rate) => (
                      <SelectItem key={String(rate.id)} value={String(rate.id)}>
                        {rate.percentage} % ({rate.name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}
            <p className="text-xs text-muted-foreground">Nová cena platí od dneška, ta dosavadní zůstane v historii.</p>
          </div>
          <div className="flex justify-end gap-2 border-t px-4 py-3">
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Zrušit
            </Button>
            <Button type="submit" size="sm" disabled={amount === null || amount <= 0 || saving}>
              Uložit cenu
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}
